// apps/client/src/audio/music.ts (title screen, 2026-10-05) — the page's one sound engine and what it plays when. The title theme plays
// from Play now (the gesture that unlocks audio) through sign-in, the creator, the towns list and the loading screen; the town music
// takes over, crossfading, once a town is on screen, and the title theme comes back on the towns list. The Game uses the same engine
// (game.sound) for its music and effects, so the page has a single AudioContext. Volumes follow settings.music / settings.sfx.
import type { Phase } from '../state/slices/core';
import { DEFAULT_SETTINGS } from '../state/slices/core';
import { titleShowing } from '../state/slices/title';
import { useGame } from '../state/store';
import type { Song } from './mixer';
import { Sound } from './sound';

let sound: Sound | null = null;
/** The page's Sound, created on first use (it makes no AudioContext until unlock()). */
export function pageSound(): Sound { return (sound ??= new Sound(useGame.getState().settings)); }

/** The title theme everywhere outside a town (the loading screen included), the town music in town. */
export function songFor(phase: Phase): Song {
  return phase === 'inTown' ? 'town' : 'title';
}

type Player = Pick<Sound, 'play' | 'setVolumes'> & Partial<Pick<Sound, 'unlock' | 'status'>>;
type Target = Pick<EventTarget, 'addEventListener' | 'removeEventListener'>;
/** main.tsx: keeps the engine's song and volumes in step with the store. Away from the title, any tap or key also (re)starts audio
 *  that is not running: the page load back from Sign in with Apple skips the title (no Play now), and iOS can suspend a context after
 *  an interruption. This is the page's one gesture unlock, in town too (the Game adds none of its own); on the title only Play now and
 *  the music switch start audio. Returns the uninstall. */
export function installMusic(s: Player = pageSound(), on: Target | null = typeof window === 'undefined' ? null : window): () => void {
  const st = useGame.getState();
  s.play(songFor(st.phase));
  s.setVolumes({ music: st.settings.music, sfx: st.settings.sfx });
  const gesture = () => {
    const st = useGame.getState();
    if (!titleShowing(st.titleStep, st.phase) && s.unlock && s.status?.().context !== 'running') unlockAudio(s as Pick<Sound, 'unlock'>);
  };
  on?.addEventListener('pointerdown', gesture);
  on?.addEventListener('keydown', gesture);
  const unsubscribe = useGame.subscribe((next, prev) => {
    if (next.phase !== prev.phase) s.play(songFor(next.phase));
    if (next.settings.music !== prev.settings.music || next.settings.sfx !== prev.settings.sfx) {
      s.setVolumes({ music: next.settings.music, sfx: next.settings.sfx });
    }
  });
  return () => { unsubscribe(); on?.removeEventListener('pointerdown', gesture); on?.removeEventListener('keydown', gesture); };
}

/** Play now (and the music switch turned on): audio may only start inside a tap, so the tap's handler calls this. */
export function unlockAudio(s: Pick<Sound, 'unlock'> = pageSound()): void {
  try { s.unlock(); } catch { /* no Web Audio in this browser: the game plays silent */ }
}

/** The level the music had before the title screen's switch turned it off, brought back when it is turned on again. */
let before: number | null = null;
/** The title screen's music switch: off sets the music volume to 0 (the same setting as Settings → Music); on restores it. */
export function setMusicOn(on: boolean, s: Pick<Sound, 'unlock'> = pageSound()): void {
  const st = useGame.getState();
  if (!on) {
    if (st.settings.music > 0) before = st.settings.music;
    st.setSettings({ music: 0 });
    return;
  }
  st.setSettings({ music: before ?? DEFAULT_SETTINGS.music });
  before = null;
  unlockAudio(s);
}
